/**
 * VoicemailScreen — list of voicemails for the resident's unit, newest first.
 *
 * Loads via voicemailsApi.list() on mount. Each row shows the visitor name,
 * a relative timestamp, the recorded duration, and a Play button. Tapping
 * play plays the audio and marks it as read (the badge on HomeScreen
 * decrements on next poll — we don't aggressively refresh the count from
 * here because the 10s polling cadence is good enough).
 *
 * Empty state: a friendly "No hay mensajes todavía" copy. Loaded but no
 * voicemails is the common case for most units.
 */
import { useCallback, useEffect, useState } from 'react';
import { useApp } from '@/state/store';
import { voicemailsApi, type Voicemail } from '@/lib/api';
import { sfx } from '@/lib/audio';
import { formatRelative } from '@/lib/format';
import { VoicemailPlayer } from '@/components/VoicemailPlayer';
import { MicSprite, ArrowSprite } from '@/components/Sprites';

export function VoicemailScreen() {
  const { back, showToast } = useApp();
  const [voicemails, setVoicemails] = useState<Voicemail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await voicemailsApi.list(100);
      setVoicemails(res.voicemails || []);
      setError(null);
    } catch (err: any) {
      setError(err?.message || 'No se pudieron cargar los mensajes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // After a voicemail is played + marked read, flip its row to "read"
  // locally so the user sees the indicator clear immediately (the
  // server's idempotent — subsequent calls keep the original read_at).
  const handlePlayed = useCallback((id: string) => {
    setVoicemails((rows) => rows.map((v) => v.id === id ? { ...v, read_at: v.read_at || new Date().toISOString() } : v));
  }, []);

  // Hardware button handlers (B = back)
  useEffect(() => {
    const onB = () => { sfx.back(); back(); };
    window.addEventListener('vicin-gb:b', onB);
    return () => window.removeEventListener('vicin-gb:b', onB);
  }, [back]);

  return (
    <div className="gb-screen">
      <div className="gb-status">
        <div className="gb-status-left">
          <span className="gb-status-dot" />
          <span className="gb-pix gb-pix-xs">MENSAJES</span>
        </div>
        <div className="gb-status-right">
          <span className="gb-pix gb-pix-xs">{voicemails.length}</span>
        </div>
      </div>

      <div className="gb-body" style={{ padding: '4px', gap: 4, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {loading && voicemails.length === 0 && (
          <div className="gb-pix gb-mid-text" style={{ textAlign: 'center', padding: 8, fontSize: 7 }}>
            Cargando…
          </div>
        )}

        {error && (
          <div className="gb-dialog" style={{ padding: 6, fontSize: 7, borderColor: 'var(--gb-dark)' }}>
            <span style={{ color: 'var(--gb-dark)' }}>{error}</span>
            <button
              type="button"
              onClick={load}
              className="gb-pix"
              style={{ display: 'block', margin: '4px auto 0', fontSize: 6, textDecoration: 'underline' }}
            >
              Reintentar
            </button>
          </div>
        )}

        {!loading && !error && voicemails.length === 0 && (
          <div className="gb-dialog" style={{ padding: 8, textAlign: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 4 }}>
              <MicSprite size={28} />
            </div>
            <div className="gb-pix gb-pix-xs" style={{ color: 'var(--gb-dark)' }}>
              NO HAY MENSAJES
            </div>
            <div className="gb-pix" style={{ fontSize: 6, marginTop: 4, color: 'var(--gb-mid)' }}>
              Cuando alguien deje un audio sin contestar, aparece acá.
            </div>
          </div>
        )}

        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4, minHeight: 0 }}>
          {voicemails.map((v) => {
            const unread = !v.read_at;
            return (
              <div
                key={v.id}
                className="gb-dialog"
                data-testid={`voicemail-row-${v.id}`}
                style={{
                  padding: 5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  borderColor: unread ? 'var(--gb-dark)' : 'var(--gb-mid)',
                  background: unread ? 'var(--gb-bg)' : 'var(--gb-mid)',
                  opacity: unread ? 1 : 0.85,
                  position: 'relative',
                }}
              >
                {/* Unread dot — the GameBoy way of saying "new": a single
                    pixel in the corner that blinks via CSS. Avoids a
                    numeric badge because voicemails are read individually,
                    not in batches. */}
                {unread && (
                  <span
                    className="gb-blink"
                    aria-label="No leído"
                    style={{
                      position: 'absolute',
                      top: 3,
                      right: 3,
                      width: 5,
                      height: 5,
                      background: 'var(--gb-dark)',
                      borderRadius: '50%',
                    }}
                  />
                )}

                <div style={{ flexShrink: 0 }}>
                  <MicSprite size={20} />
                </div>

                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <div
                    className="gb-pix"
                    style={{
                      fontSize: 7,
                      fontWeight: unread ? 'bold' : 'normal',
                      color: 'var(--gb-dark)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {v.visitor_name || 'Visitante'}
                  </div>
                  <div
                    className="gb-pix"
                    style={{
                      fontSize: 5,
                      color: 'var(--gb-mid)',
                      display: 'flex',
                      gap: 4,
                      alignItems: 'center',
                    }}
                  >
                    <span>{formatRelative(v.created_at)}</span>
                    {v.duration_ms != null && (
                      <>
                        <span>·</span>
                        <span>{Math.max(1, Math.round(v.duration_ms / 1000))}s</span>
                      </>
                    )}
                  </div>
                </div>

                <VoicemailPlayer
                  voicemailId={v.id}
                  durationMs={v.duration_ms}
                  onPlayed={() => handlePlayed(v.id)}
                />
              </div>
            );
          })}
        </div>

        <div className="gb-pix gb-pix-xs gb-mid-text" style={{ textAlign: 'center', fontSize: 5, marginTop: 1 }}>
          [B] VOLVER
        </div>
      </div>
    </div>
  );
}